import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CommentDataResult, SessionDataResult, SessionState } from "interfaces/sessions";
import { CustomMetric, CustomDetail, SessionMetric, SessionV2 } from '../../../../interfaces/db';
import { ReduxSafeSessionV2 } from '../audio/audio-slice';

// Redux-safe version of SessionState
export type ReduxSafeSessionState = Omit<SessionState, 'started_at'> & {
  started_at: string; // ISO string
};

// Redux-safe version of CustomMetric
export type ReduxSafeCustomMetric = Omit<CustomMetric, 'scheduled_start' | 'calculated_as_of'> & {
  scheduled_start: string; // ISO string
  calculated_as_of: string; // ISO string
};

// Redux-safe version of CustomDetail
export type ReduxSafeCustomDetail = Omit<CustomDetail, 'scheduled_start' | 'calculated_as_of'> & {
  scheduled_start: string; // ISO string
  calculated_as_of: string; // ISO string
};

// Redux-safe version of SessionDataResult
export type ReduxSafeSessionDataResult = Omit<SessionDataResult, 'session' | 'session_state' | 'custom_metric' | 'custom_detail'> & {
  session: ReduxSafeSessionV2;
  session_state: ReduxSafeSessionState | null;
  custom_metric: ReduxSafeCustomMetric | null;
  custom_detail: ReduxSafeCustomDetail | null;
};

// Conversion functions
export function toReduxSafeSessionState(state: SessionState): ReduxSafeSessionState {
  return {
    ...state,
    started_at: state.started_at instanceof Date ? state.started_at.toISOString() : 
              (typeof state.started_at === 'string' ? state.started_at : new Date().toISOString())
  };
}

export function fromReduxSafeSessionState(state: ReduxSafeSessionState): SessionState {
  return {
    ...state,
    started_at: new Date(state.started_at)
  };
}

export function toReduxSafeCustomMetric(metric: CustomMetric): ReduxSafeCustomMetric {
  return {
    ...metric,
    scheduled_start: metric.scheduled_start instanceof Date ? metric.scheduled_start.toISOString() : 
                     (typeof metric.scheduled_start === 'string' ? metric.scheduled_start : new Date().toISOString()),
    calculated_as_of: metric.calculated_as_of instanceof Date ? metric.calculated_as_of.toISOString() : 
                      (typeof metric.calculated_as_of === 'string' ? metric.calculated_as_of : new Date().toISOString())
  };
}

export function fromReduxSafeCustomMetric(metric: ReduxSafeCustomMetric): CustomMetric {
  return {
    ...metric,
    scheduled_start: new Date(metric.scheduled_start),
    calculated_as_of: new Date(metric.calculated_as_of)
  };
}

export function toReduxSafeCustomDetail(detail: CustomDetail): ReduxSafeCustomDetail {
  return {
    ...detail,
    scheduled_start: detail.scheduled_start instanceof Date ? detail.scheduled_start.toISOString() : 
                     (typeof detail.scheduled_start === 'string' ? detail.scheduled_start : new Date().toISOString()),
    calculated_as_of: detail.calculated_as_of instanceof Date ? detail.calculated_as_of.toISOString() : 
                      (typeof detail.calculated_as_of === 'string' ? detail.calculated_as_of : new Date().toISOString())
  };
}

export function fromReduxSafeCustomDetail(detail: ReduxSafeCustomDetail): CustomDetail {
  return {
    ...detail,
    scheduled_start: new Date(detail.scheduled_start),
    calculated_as_of: new Date(detail.calculated_as_of)
  };
}

export function toReduxSafeSessionDataResult(result: SessionDataResult): ReduxSafeSessionDataResult {
  return {
    session: {
      ...result.session,
      started_at: result.session.started_at instanceof Date ? result.session.started_at.toISOString() : 
                  (typeof result.session.started_at === 'string' ? result.session.started_at : new Date().toISOString())
    } as ReduxSafeSessionV2,
    session_state: result.session_state ? toReduxSafeSessionState(result.session_state) : null,
    custom_metric: result.custom_metric ? toReduxSafeCustomMetric(result.custom_metric) : null,
    custom_detail: result.custom_detail ? toReduxSafeCustomDetail(result.custom_detail) : null,
    // Copy other fields explicitly to avoid Date objects
    session_agents: result.session_agents,
    session_details: result.session_details,
    prospect_info: result.prospect_info,
    comments: result.comments,
    call_metric: result.call_metric,
    sales_metric: result.sales_metric,
    counterpart_data: result.counterpart_data,
    turns: result.turns,
    review_status: result.review_status
  };
}

export function fromReduxSafeSessionDataResult(result: ReduxSafeSessionDataResult): SessionDataResult {
  return {
    ...result,
    session: {
      ...result.session,
      started_at: new Date(result.session.started_at)
    } as SessionV2,
    session_state: result.session_state ? fromReduxSafeSessionState(result.session_state) : null,
    custom_metric: result.custom_metric ? fromReduxSafeCustomMetric(result.custom_metric) : null,
    custom_detail: result.custom_detail ? fromReduxSafeCustomDetail(result.custom_detail) : null
  };
}

interface RequestState {
  isLoading: boolean;
  error: string | null;
}

export enum SessionRequestType {
  SUMMARY = 'summary',
  EMAIL = 'email',
  COACHING = 'coaching',
  CUSTOM_SCORE = 'custom_score'
}

interface SessionRequestStates {
  [SessionRequestType.SUMMARY]?: RequestState;
  [SessionRequestType.EMAIL]?: RequestState;
  [SessionRequestType.COACHING]?: RequestState;
  [SessionRequestType.CUSTOM_SCORE]?: RequestState;
}

export interface SessionDetailsState {
  sessionIdToDetails: {
    [sessionId: string]: {
      data: ReduxSafeSessionDataResult | null;
      isLoading: boolean;
      error: string | null;
      requestStates: SessionRequestStates;
    }
  }
}

const initialRequestState: RequestState = {
  isLoading: false,
  error: null
};

const initialSessionRequestStates: SessionRequestStates = {
  [SessionRequestType.SUMMARY]: initialRequestState,
  [SessionRequestType.EMAIL]: initialRequestState,
  [SessionRequestType.COACHING]: initialRequestState,
  [SessionRequestType.CUSTOM_SCORE]: initialRequestState
};

const initialState: SessionDetailsState = {
  sessionIdToDetails: {}
};

const processData = (data: ReduxSafeSessionDataResult): ReduxSafeSessionDataResult => {
  const comments = data.comments ?? []
  const turns = (data.turns ?? []).sort((a, b) => a.start - b.start).filter((turn) => turn.text !== '')
  const updatedComments = comments.map((comment) => {
    if (!comment.start || !comment.end) return comment;
    const turn = turns.findIndex((turn) => comment.start && comment.end && turn.start && turn.end && Math.floor(turn.start) <= comment.start && Math.ceil(turn.end) >= comment.end && turn.text !== '');
    if (turn === -1) return comment;
    return { ...comment, transcriptIdx: turn };
  })
  return {
    ...data,
    comments: updatedComments,
    turns: turns
  }
}

export const sessionDetailsSlice = createSlice({
  name: 'sessionDetails',
  initialState,
  reducers: {
    startPendingComment: (state, action: PayloadAction<{sessionId: string, comment: CommentDataResult}>) => {
      const { sessionId, comment } = action.payload;
      const comments = state.sessionIdToDetails[sessionId].data?.comments ?? [];
      let idx = comments.findIndex((value) => (value.transcriptIdx ?? -1) > (comment.transcriptIdx ?? -1));
      if (idx === -1) idx = comments.length;
  
      const updatedComments = [
        ...comments.slice(0, idx),
        comment,
        ...comments.slice(idx)
      ];
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        data: {
          ...state.sessionIdToDetails[sessionId].data!,
          comments: updatedComments
        },
      };
    },
    updateComment: (state, action: PayloadAction<{sessionId: string, commentId: string, partialComment: Partial<CommentDataResult>}>) => {
      const { sessionId, commentId, partialComment } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        data: {
          ...state.sessionIdToDetails[sessionId].data!,
          comments: state.sessionIdToDetails[sessionId].data!.comments!.map((comment) => comment.comment_id === commentId ? { ...comment, ...partialComment } : comment)
        },
      };
    },
    removeCommentId: (state, action: PayloadAction<{sessionId: string, commentId: string}>) => {
      const { sessionId, commentId } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        data: {
          ...state.sessionIdToDetails[sessionId].data!,
          comments: state.sessionIdToDetails[sessionId].data!.comments!.filter((comment) => comment.comment_id !== commentId)
        },
      };
    },
    startLoading: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        isLoading: true,
        error: null
      };
    },
    stopLoading: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        isLoading: false
      };
    },
    loadSuccess: (state, action: PayloadAction<{ sessionId: string; data: ReduxSafeSessionDataResult }>) => {
      const { sessionId, data } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        data: processData(data),
        isLoading: false,
        error: null,
        requestStates: state.sessionIdToDetails[sessionId]?.requestStates ?? initialSessionRequestStates
      };
    },
    loadError: (state, action: PayloadAction<{ sessionId: string; error: string }>) => {
      const { sessionId, error } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        isLoading: false,
        error
      };
    },
    startRequest: (state, action: PayloadAction<{ sessionId: string; requestType: SessionRequestType }>) => {
      const { sessionId, requestType } = action.payload;
      if (!state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId] = {
          data: null,
          isLoading: false,
          error: null,
          requestStates: initialSessionRequestStates
        };
      }
      state.sessionIdToDetails[sessionId].requestStates[requestType] = {
        isLoading: true,
        error: null
      };
    },
    stopRequest: (state, action: PayloadAction<{ sessionId: string; requestType: SessionRequestType }>) => {
      const { sessionId, requestType } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].requestStates[requestType] = {
          isLoading: false,
          error: null 
        };
      }
    },
    updateSessionMetric: (state, action: PayloadAction<{ 
      sessionId: string; 
      sessionMetric: Partial<SessionMetric>;
    }>) => {
      const { sessionId, sessionMetric } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].data = {  
          ...state.sessionIdToDetails[sessionId].data!,
          sales_metric: {
            ...state.sessionIdToDetails[sessionId].data!.sales_metric!,
            ...sessionMetric
          }
        };
      }
    },
    updateCustomMetric: (state, action: PayloadAction<{ 
      sessionId: string; 
      metric: CustomMetric;
      detail: CustomDetail;
    }>) => {
      const { sessionId, metric, detail } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].data = {  
          ...state.sessionIdToDetails[sessionId].data!,
          custom_detail: detail ? toReduxSafeCustomDetail(detail) : null,
          custom_metric: metric ? toReduxSafeCustomMetric(metric) : null,
        };
      }
    },
    requestError: (state, action: PayloadAction<{ 
      sessionId: string; 
      requestType: SessionRequestType;
      error: string;
    }>) => {
      const { sessionId, requestType, error } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].requestStates[requestType] = {
          isLoading: false,
          error
        };
      }
    },
    clearSessionData: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      delete state.sessionIdToDetails[sessionId];
    },
    clearOldSessions: (state, action: PayloadAction<{ keepSessionIds: string[], maxSessions?: number }>) => {
      const { keepSessionIds, maxSessions = 10 } = action.payload;
      const keepSet = new Set(keepSessionIds);
      
      // Get all session IDs and sort by when they were last accessed (assuming newer ones are accessed more recently)
      const allSessionIds = Object.keys(state.sessionIdToDetails);
      const sessionsToKeep = new Set<string>();
      
      // Always keep the sessions in keepSessionIds
      keepSessionIds.forEach(id => sessionsToKeep.add(id));
      
      // If we have more sessions than maxSessions, remove the oldest ones
      if (allSessionIds.length > maxSessions) {
        // Keep only the most recent sessions up to maxSessions
        let kept = keepSessionIds.length;
        for (let i = allSessionIds.length - 1; i >= 0 && kept < maxSessions; i--) {
          const sessionId = allSessionIds[i];
          if (!keepSet.has(sessionId)) {
            sessionsToKeep.add(sessionId);
            kept++;
          }
        }
        
        // Clear sessions that are not in sessionsToKeep
        allSessionIds.forEach(sessionId => {
          if (!sessionsToKeep.has(sessionId)) {
            delete state.sessionIdToDetails[sessionId];
          }
        });
      }
    },
  }
})

export const { 
  startLoading, 
  stopLoading,
  loadSuccess, 
  loadError,
  startRequest,
  stopRequest,
  updateSessionMetric,
  updateCustomMetric,
  requestError,
  clearSessionData,
  clearOldSessions,
  startPendingComment,
  updateComment,
  removeCommentId,
} = sessionDetailsSlice.actions;

export default sessionDetailsSlice.reducer;
